import { z } from 'zod';

import {
  GeoJsonGeometrySchema,
  GeoJsonLineStringGeometrySchema,
  HexColorSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LocalizedTextSchema,
  NonEmptyTextSchema,
  PositionSchema,
} from './common.js';

export const EditionTypeSchema = z.enum(['summer', 'winter']);
export const EditionPhaseSchema = z.enum(['preparation', 'arrival', 'live', 'after']);

export const EditionMetadataSchema = z
  .object({
    id: IdentifierSchema,
    title: LocalizedTextSchema,
    type: EditionTypeSchema,
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    timezone: NonEmptyTextSchema.max(64),
    arrivalStartsAt: IsoDateTimeSchema.optional(),
  })
  .strict();

export const StageSchema = z
  .object({
    id: IdentifierSchema,
    name: LocalizedTextSchema,
    shortName: NonEmptyTextSchema.max(40).optional(),
    color: HexColorSchema,
    mapFeatureId: IdentifierSchema.optional(),
  })
  .strict();

export const TimetableActSchema = z
  .object({
    id: IdentifierSchema,
    title: LocalizedTextSchema,
    stageId: IdentifierSchema,
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    description: LocalizedTextSchema.optional(),
    tags: z.array(NonEmptyTextSchema.max(60)).max(20).optional(),
    imageAssetId: IdentifierSchema.optional(),
  })
  .strict();

export const MapFeatureKindSchema = z.enum(['poi', 'stage', 'area', 'path', 'landmark']);

export const MapCategorySchema = z
  .object({
    id: IdentifierSchema,
    label: LocalizedTextSchema,
    color: HexColorSchema.optional(),
    icon: NonEmptyTextSchema.max(80).optional(),
  })
  .strict();

export const MapFeaturePropertiesSchema = z
  .object({
    id: IdentifierSchema,
    kind: MapFeatureKindSchema,
    label: LocalizedTextSchema,
    categoryId: IdentifierSchema.optional(),
    stageId: IdentifierSchema.optional(),
    description: LocalizedTextSchema.optional(),
    icon: NonEmptyTextSchema.max(80).optional(),
    accessible: z.boolean().optional(),
  })
  .strict();

export const MapFeatureSchema = z
  .object({
    type: z.literal('Feature'),
    id: IdentifierSchema,
    geometry: GeoJsonGeometrySchema,
    properties: MapFeaturePropertiesSchema,
  })
  .strict();

export const MapFeatureCollectionSchema = z
  .object({
    type: z.literal('FeatureCollection'),
    features: z.array(MapFeatureSchema),
  })
  .strict();

export const MapOverlaySchema = z
  .object({
    assetId: IdentifierSchema,
    corners: z.tuple([PositionSchema, PositionSchema, PositionSchema, PositionSchema]),
    opacity: z.number().finite().min(0).max(1),
    rightsConfirmed: z.boolean(),
    rightsNote: NonEmptyTextSchema.max(500).optional(),
  })
  .strict();

export const RoutingNodeSchema = z
  .object({
    id: IdentifierSchema,
    position: PositionSchema,
    mapFeatureId: IdentifierSchema.optional(),
  })
  .strict();

export const RoutingEdgeSchema = z
  .object({
    id: IdentifierSchema,
    fromNodeId: IdentifierSchema,
    toNodeId: IdentifierSchema,
    distanceMeters: z.number().finite().positive(),
    geometry: GeoJsonLineStringGeometrySchema,
    bidirectional: z.boolean().optional(),
    accessible: z.boolean().optional(),
  })
  .strict();

export const RoutingGraphSchema = z
  .object({
    nodes: z.array(RoutingNodeSchema),
    edges: z.array(RoutingEdgeSchema),
  })
  .strict();

export const MapInitialViewSchema = z
  .object({
    center: PositionSchema,
    zoom: z.number().finite().min(0).max(24),
    minZoom: z.number().finite().min(0).max(24).optional(),
    maxZoom: z.number().finite().min(0).max(24).optional(),
  })
  .strict();

export const MapManifestV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    categories: z.array(MapCategorySchema),
    features: MapFeatureCollectionSchema,
    routing: RoutingGraphSchema,
    overlay: MapOverlaySchema.optional(),
    initialView: MapInitialViewSchema.optional(),
  })
  .strict();

export const EditionContentV1Schema = z
  .object({
    edition: EditionMetadataSchema,
    stages: z.array(StageSchema),
    timetable: z.array(TimetableActSchema),
    map: MapManifestV1Schema,
  })
  .strict();

export const EditionReleaseV1Schema = EditionContentV1Schema.extend({
  schemaVersion: z.literal(1),
  releaseId: IdentifierSchema,
  version: z.number().int().positive(),
  publishedAt: IsoDateTimeSchema,
  contentHash: z.string().regex(/^[a-f0-9]{64}$/, 'Expected a lowercase SHA-256 hash.'),
}).strict();

export const EditionDraftV1Schema = EditionContentV1Schema.extend({
  schemaVersion: z.literal(1),
  draftId: IdentifierSchema,
  updatedAt: IsoDateTimeSchema,
  baseReleaseId: IdentifierSchema.optional(),
}).strict();

export type EditionType = z.infer<typeof EditionTypeSchema>;
export type EditionPhase = z.infer<typeof EditionPhaseSchema>;
export type EditionMetadata = z.infer<typeof EditionMetadataSchema>;
export type Stage = z.infer<typeof StageSchema>;
export type TimetableAct = z.infer<typeof TimetableActSchema>;
export type MapFeatureKind = z.infer<typeof MapFeatureKindSchema>;
export type MapCategory = z.infer<typeof MapCategorySchema>;
export type MapFeatureProperties = z.infer<typeof MapFeaturePropertiesSchema>;
export type MapFeature = z.infer<typeof MapFeatureSchema>;
export type MapFeatureCollection = z.infer<typeof MapFeatureCollectionSchema>;
export type MapOverlay = z.infer<typeof MapOverlaySchema>;
export type RoutingNode = z.infer<typeof RoutingNodeSchema>;
export type RoutingEdge = z.infer<typeof RoutingEdgeSchema>;
export type RoutingGraph = z.infer<typeof RoutingGraphSchema>;
export type MapInitialView = z.infer<typeof MapInitialViewSchema>;
export type MapManifestV1 = z.infer<typeof MapManifestV1Schema>;
export type EditionContentV1 = z.infer<typeof EditionContentV1Schema>;
export type EditionReleaseV1 = z.infer<typeof EditionReleaseV1Schema>;
export type EditionDraftV1 = z.infer<typeof EditionDraftV1Schema>;
